import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { setItemStorage, getItemStorage, removeItemStorage } from "@/core/helpers/storage";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { route } from '~ziggy-js';
import { useApiStore } from "@/store/apiStore";

/** Skip remote token check when features are already warm (Herd FE→BE proxy is expensive). */
const VERIFY_AUTH_TTL_MS = 3 * 60 * 1000;
let lastVerifyAuthAt = 0;

export interface User {
  id: any;
  name: string;
  features: any;
  email: string;
  password: string;
  token: string;
  google2fa_is_active: boolean;
  is_active: boolean;
  properties: any;
  user_categories: any;
}

interface ListItemProperty {
  id: string;
  name: string;
}

export interface UserAuthInfo {
  errors: unknown;
  user: any; //User;
  user_property_id: string;
  userFeatures: any;
  userProperties: Array<ListItemProperty>;
  isAuthenticated: boolean;
  counter: number;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = getItemStorage("usr") || {};
  user_property_id = getItemStorage("usr_prop") || '';
  userFeatures = this.user['features'] || {};
  userProperties = this.user['properties'] as ListItemProperty[] || [];
  isAuthenticated = JwtService.hasAuth();
  counter = 0;

  /**
   * Get current user object
   * @returns User
   */
  get currentUser() {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors() {
    return this.errors;
  }

  /**
 * Get user detail
 * @returns objects
 */
  get getUserDetail() {
    const userDetail: any = this.user || getItemStorage("usr");
    return userDetail;
  }

  /**
  * Get user features
  * @returns objects
  */
  get getUserFeatures() {
    return this.userFeatures;
  }

  /**
 * Get user properties list
 * @returns array
 */
  get getUserPropertiesList() {
    return this.userProperties;
  }

  /**
   * Get current user property
   * @returns string
   */
  get getUserProperty() {
    return this.user_property_id;
  }

  get getUserVerifyCounter() {
    return this.counter;
  }

  @Mutation
  ["setCounter"]() {
    this.counter++;
  }

  @Mutation
  [Mutations.SET_ERROR](error: any) {
    this.errors = { ...error };
  }

  @Mutation
  [Mutations.SET_AUTH](user: any) {
    // P5-01: payload is user detail only (no API token in the browser).
    const profile = (user && user.features !== undefined)
      ? user
      : (user?.data ?? user);
    this.isAuthenticated = true;
    this.user = profile;
    this.userFeatures = profile?.features || {};
    this.userProperties = profile?.properties as ListItemProperty[] || [];
    this.errors = {};
    setItemStorage('usr', profile);
    JwtService.saveToken();
    lastVerifyAuthAt = Date.now();
  }

  @Mutation
  [Mutations.SET_USER](user: any) {
    this.user = user;
    this.userFeatures = user?.features || {};
    this.userProperties = user?.properties as ListItemProperty[] || [];
    setItemStorage('usr', user);
    lastVerifyAuthAt = Date.now();
  }

  @Mutation
  [Mutations.SET_USER_FEATURES](features: any) {
    this.userFeatures = features;
  }

  @Mutation
  [Mutations.SET_USER_PROPERTY](user_property_id: any) {
    setItemStorage('usr_prop', user_property_id);
    this.user_property_id = user_property_id;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user['password'] = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {};
    this.userFeatures = {};
    this.user_property_id = '';
    this.errors = [];
    lastVerifyAuthAt = 0;
    JwtService.destroyToken();
    removeItemStorage('usr');
    removeItemStorage('usr_prop');
    useApiStore().clearCache();
  }

  @Action
  [Actions.LOGIN](credentials: any) {
    return ApiService.post(route('auth.login'), credentials) // '/auth/gettoken'
      .then((response) => {
        // console.log(data);
        if (response.data) {
          this.context.commit(Mutations.SET_AUTH, response.data);
        }
      })
      .catch((error) => {
        this.context.commit(Mutations.SET_ERROR, error?.data);
      });
  }

  @Action
  [Actions.AUTH_LOGIN](user: any) {
    this.context.commit(Mutations.SET_AUTH, user);
  }

  @Action
  [Actions.LOGOUT]() {
    const finish = () => this.context.commit(Mutations.PURGE_AUTH);
    if (!JwtService.hasAuth()) {
      finish();
      return Promise.resolve();
    }
    return ApiService.post(route('auth.logout'), {})
      .catch(() => undefined)
      .finally(() => finish());
  }

  @Action
  [Actions.REGISTER](credentials: any) {
    return ApiService.post("register", credentials)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch((error) => {
        this.context.commit(Mutations.SET_ERROR, error?.data);
      });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload: any) {
    return ApiService.post(route('auth.forgot-password'), payload)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
      })
      .catch((error) => {
        this.context.commit(Mutations.SET_ERROR, error?.data);
      });
  }

  @Action
  [Actions.VERIFY_AUTH](payload: any) {
    if (JwtService.hasAuth()) {
      ApiService.setHeader();
      const features = this.context.getters.getUserFeatures;
      const featuresWarm = !!(features && typeof features === "object" && Object.keys(features).length > 0);
      const recentlyVerified = lastVerifyAuthAt > 0 && (Date.now() - lastVerifyAuthAt) < VERIFY_AUTH_TTL_MS;
      if (this.context.getters.isUserAuthenticated && featuresWarm && recentlyVerified) {
        return Promise.resolve();
      }

      return ApiService.get(route('auth.verifytoken'))
        .then((response) => {
          const resp = response.data;
          if (resp) {
            this.context.commit(Mutations.SET_USER, resp.data);
            JwtService.saveToken();
            lastVerifyAuthAt = Date.now();
          }
        })
        .catch((error) => {
          const status = error?.response?.status;
          // Only purge on hard auth failures. Proxy timeouts on Herd must not
          // wipe the session or force a long empty-state reload.
          if (status === 401 || status === 403) {
            lastVerifyAuthAt = 0;
            this.context.commit(Mutations.SET_ERROR, error?.data);
            this.context.commit(Mutations.PURGE_AUTH);
          }
        });
    }

    lastVerifyAuthAt = 0;
    this.context.commit(Mutations.PURGE_AUTH);
    return Promise.resolve();
  }
}
